import React from 'react';

import type { IntlShape } from 'react-intl-next';

import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	PluginKey,
	type EditorState,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { SuggestionIconButton } from '../ui/components/SuggestionIconButton';
import { buildSuggestionMaps } from '../ui/utils/buildSuggestionMaps';
import { updateSuggestions } from '../ui/utils/updateSuggestions';

export type AiSuggestionsPluginState = {
	baseLocalIdBySuggestionId: Map<string, string>;
	decorations: DecorationSet;
	orderedSuggestionIds: string[];
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
};

export const aiSuggestionsPluginKey = new PluginKey<AiSuggestionsPluginState>(
	'aiSuggestionsPlugin',
);

export type AiSuggestionsMeta =
	| { action: 'SET_SUGGESTIONS'; suggestions: Suggestion[] }
	| { action: 'CLEAR_SUGGESTIONS' }
	| { action: 'SELECT_SUGGESTION'; id: string | undefined }
	| { action: 'REMOVE_SUGGESTION'; id: string };

function calculateSuggestionsDecorations({
	doc,
	suggestionById,
	suggestionIdsByBaseLocalId,
	api,
	intl,
	selectedSuggestion,
	nodeViewPortalProviderAPI,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	doc: PMNode;
	intl: IntlShape | undefined;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
}): DecorationSet {
	if (suggestionById.size === 0) {
		return DecorationSet.empty;
	}

	const decorations: Decoration[] = [];

	doc.descendants((node, pos) => {
		const { localId } = node.attrs;
		if (localId) {
			const suggestionsAtLocalId = suggestionIdsByBaseLocalId.get(localId);
			if (suggestionsAtLocalId?.size) {
				const firstSuggestionId = suggestionsAtLocalId.values().next().value;
				const firstSuggestion = firstSuggestionId
					? suggestionById.get(firstSuggestionId)
					: undefined;

				if (firstSuggestion) {
					const element = document.createElement('span');
					element.setAttribute(
						'style',
						convertToInlineCss({
							position: 'relative',
							width: '100%',
							display: 'block',
						}),
					);

					const key = crypto.randomUUID();
					const isSelected = Boolean(
						selectedSuggestion && suggestionsAtLocalId.has(selectedSuggestion),
					);

					decorations.push(
						Decoration.widget(
							pos,
							() => {
								nodeViewPortalProviderAPI.render(
									() => (
										<SuggestionIconButton
											suggestion={firstSuggestion}
											isSelected={isSelected}
											api={api}
											intl={intl}
										/>
									),
									element,
									key,
								);
								return element;
							},
							{
								side: 1,
								key,
								destroy: () => {
									// Clean up properly
									// nodeViewPortalProviderAPI.remove(key);
								},
							},
						),
					);
				}
			}
		}
		return false;
	});

	if (!decorations.length) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
}

const {
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
} = buildSuggestionMaps(undefined);

const initState: AiSuggestionsPluginState = {
	decorations: DecorationSet.empty,
	selectedSuggestion: undefined,
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
};

export function createPlugin({
	api,
	intl,
	contentAri,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
	intl?: IntlShape;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin<AiSuggestionsPluginState> {
	return new SafePlugin<AiSuggestionsPluginState>({
		key: aiSuggestionsPluginKey,
		state: {
			init(_config, _state: EditorState): AiSuggestionsPluginState {
				return initState;
			},
			apply(
				tr: ReadonlyTransaction,
				pluginState: AiSuggestionsPluginState,
				_oldState: EditorState,
				newState: EditorState,
			): AiSuggestionsPluginState {
				const meta = tr.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined;
				let nextState = pluginState;

				if (meta) {
					switch (meta.action) {
						case 'SET_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								suggestions: meta.suggestions,
								doc: newState.doc,
							});

							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById,
									suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: pluginState.selectedSuggestion,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						case 'CLEAR_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								doc: newState.doc,
								suggestions: [],
							});
							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								selectedSuggestion: undefined,
								decorations: DecorationSet.empty,
							};
							break;
						}
						case 'SELECT_SUGGESTION':
							nextState = {
								...pluginState,
								selectedSuggestion: meta.id,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById: pluginState.suggestionById,
									suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: meta.id,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						case 'REMOVE_SUGGESTION': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = pluginState;
							const removedId = meta.id;
							const suggestion = suggestionById.get(removedId);

							if (!suggestion) {
								nextState = {
									...pluginState,
									selectedSuggestion: undefined,
									decorations: calculateSuggestionsDecorations({
										doc: newState.doc,
										suggestionById,
										suggestionIdsByBaseLocalId,
										api,
										intl,
										selectedSuggestion: undefined,
										nodeViewPortalProviderAPI,
									}),
								};
								break;
							}

							const nextSuggestionById = new Map(suggestionById);
							suggestionById.delete(removedId);

							const baseLocalId = baseLocalIdBySuggestionId.get(removedId);
							baseLocalIdBySuggestionId.delete(removedId);

							if (baseLocalId) {
								suggestionIdsByBaseLocalId.get(baseLocalId)?.delete(removedId);
							}

							nextState = {
								...pluginState,
								suggestionById: nextSuggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds: orderedSuggestionIds.filter((id) => id !== removedId),
								selectedSuggestion: undefined,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById: nextSuggestionById,
									suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: undefined,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						default:
							break;
					}
				} else {
					nextState = {
						...pluginState,
						decorations: pluginState.decorations.map(tr.mapping, tr.doc),
					};
				}

				return nextState;
			},
		},
		props: {
			decorations(state: EditorState) {
				return aiSuggestionsPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
			},
		},
		view: () => {
			const abortController = new AbortController();
			if (contentAri && api) {
				updateSuggestions({ contentAri, api, generate: false, signal: abortController.signal });
			}
			return {
				destroy: () => {
					abortController.abort();
				},
			};
		},
	});
}
